import { Fragment, useRef } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { events } from '../../../hooks/tracking/analytics';
import { localizeDate } from '../../../utils/datetime';
import { Flex, FlexColumn } from '../../Flex';
import {
  ChatMessage as ChatMessageType,
  ApprovalSubmissionResult,
  OnUserInteraction,
  ToolRendererFunction,
} from '../types';
import FeedbackButtons from '../../FeedbackButtons';
import { fontSize, radius, space, componentToken } from '../../../style/utils';
import CopyButton from './CopyButton';
import MessageContextChips from './MessageContextChips';
import TextMessageRenderer from './TextMessageRenderer';
import ToolInvocationRenderer from './ToolInvocationRenderer';
import ToolCallGroup from './ToolCallGroup';
import ThoughtProcess from './ThoughtProcess';
import {
  countToolInvocations,
  GroupedPart,
  groupToolParts,
  latestInvocationsById,
  shouldCollapseSegmentChain,
  shouldWrapChainInThoughtProcess,
  splitTurnSegments,
} from './messageParts';

const Wrapper = styled(Flex)<{ $maxWidth?: number }>`
  align-items: flex-start;
  gap: ${space('sm')};
  ${({ $maxWidth }) =>
    $maxWidth &&
    `
    max-width: ${$maxWidth}px;
    width: 100%;
    margin: 0 auto;
  `}

  &:hover .user-message-meta {
    opacity: 1;
  }

  &:hover .meta-time {
    opacity: 1;
  }
`;

const MessageColumn = styled(FlexColumn)<{ $isUser: boolean }>`
  gap: ${space('xs')};
  ${({ $isUser }) =>
    $isUser
      ? `
    width: fit-content;
    max-width: 100%;
    margin: 0 0 0 auto;
    align-items: flex-end;
    `
      : `
    flex: 1;
    min-width: 0;
    `}
`;

const MessageMeta = styled(Flex)<{ $isUser: boolean }>`
  align-items: center;
  justify-content: flex-end;
  flex-direction: ${({ $isUser }) => ($isUser ? 'row' : 'row-reverse')};
  gap: ${space('xs')};
  padding: ${space(0, 'xxs')};
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.tertiary};
  opacity: ${({ $isUser }) => ($isUser ? 0 : 1)};
  transition: opacity 0.15s ease-in-out;
`;

const MetaTime = styled.span<{ $hoverReveal: boolean }>`
  cursor: default;
  ${({ $hoverReveal }) =>
    $hoverReveal && 'opacity: 0; transition: opacity 0.15s ease-in-out;'}
`;

type MessageTimeProps = {
  createdAt: string;
  hoverReveal: boolean;
};

const MessageTime = ({ createdAt, hoverReveal }: MessageTimeProps) => {
  const localized = localizeDate(createdAt);
  return (
    <Tooltip
      title={localized.format('L LT')}
      enterDelay={500}
      enterNextDelay={500}
    >
      <MetaTime className="meta-time" $hoverReveal={hoverReveal}>
        {localized.fromNow()}
      </MetaTime>
    </Tooltip>
  );
};

type MessageMetaBarProps = {
  createdAt: string;
  content: string;
  isUser: boolean;
};

const MessageMetaBar = ({
  createdAt,
  content,
  isUser,
}: MessageMetaBarProps) => (
  <MessageMeta
    $isUser={isUser}
    className={isUser ? 'user-message-meta' : undefined}
  >
    <MessageTime createdAt={createdAt} hoverReveal={!isUser} />
    <CopyButton content={content} ariaLabel="Copy message" />
  </MessageMeta>
);

const Message = styled(FlexColumn)<{ $isUser: boolean }>`
  background: ${({ theme, $isUser }) =>
    $isUser ? theme.palette.surface.base : 'transparent'};
  padding: ${({ $isUser }) => ($isUser ? space('mdMinus', 'md') : '0')};
  border-radius: ${radius('lg')};
  border: ${({ $isUser, theme }) =>
    $isUser ? `1px solid ${theme.palette.border.default}` : 'none'};
  ${({ $isUser }) =>
    $isUser
      ? 'width: fit-content; margin: 0 0 0 auto;'
      : 'flex: 1; min-width: 0;'}
  gap: ${space('sm')};
`;

const UserMessage = styled.span`
  font-size: ${fontSize('base')};
  unicode-bidi: plaintext;
`;

const ToolInvocationAnchor = styled(FlexColumn)`
  &.ai-chat-tool-call-highlight {
    border: 1px solid ${componentToken('aiChat.messageBorder')};
    background: ${({ theme }) => theme.palette.surface.hover};
    border-radius: ${radius('lg')};
    padding: ${space('sm')};
  }
`;

UserMessage.defaultProps = {
  dir: 'auto',
};

type MessagePartProps = {
  part: GroupedPart;
  partIndex: number;
  actionsIdx: number;
  isUser: boolean;
  message: ChatMessageType;
  showMetaBar: boolean;
  isDebug?: boolean;
  onApproveToolUse?: (
    val: boolean,
    interruptId?: string,
    newArgs?: Record<string, unknown>,
  ) => Promise<ApprovalSubmissionResult>;
  onApplyFrontendTool?: (
    toolCallId: string,
    toolName: string,
    args: Record<string, unknown>,
  ) => void | Promise<void>;
  onRejectFrontendTool?: (toolCallId: string) => void | Promise<void>;
  onUserInteraction?: OnUserInteraction;
  interactionsDisabled?: boolean;
  getToolRenderer?: (toolName?: string) => ToolRendererFunction | null;
  contentMaxWidth?: number;
  showToolErrorDetails?: boolean;
  toolGroupExpandState?: Map<string, boolean>;
  isCollapsed?: boolean;
};

const MessagePart = ({
  part,
  partIndex,
  actionsIdx,
  isUser,
  message,
  showMetaBar,
  isDebug = false,
  onApproveToolUse,
  onApplyFrontendTool,
  onRejectFrontendTool,
  onUserInteraction,
  interactionsDisabled,
  getToolRenderer,
  contentMaxWidth,
  showToolErrorDetails = false,
  toolGroupExpandState,
  isCollapsed = false,
}: MessagePartProps) => {
  if (part.type === 'tool-group') {
    const ids = latestInvocationsById(part.invocations).map(
      (inv) => inv.toolCallId,
    );
    const persistedExpanded = ids
      .map((id) => toolGroupExpandState?.get(id))
      .find((value) => value !== undefined);
    return (
      <ToolCallGroup
        invocations={part.invocations}
        persistedExpanded={persistedExpanded}
        isCollapsed={isCollapsed}
        onExpandedChange={(expanded) => {
          ids.forEach((id) => toolGroupExpandState?.set(id, expanded));
        }}
      />
    );
  }

  if (part.type === 'text') {
    return isUser ? (
      <UserMessage>
        {message.context && <MessageContextChips context={message.context} />}
        {part.text}
      </UserMessage>
    ) : (
      <TextMessageRenderer text={part.text} />
    );
  }

  if (part.type === 'tool-invocation') {
    const { createdAt, content } = message;
    return (
      <Fragment>
        {actionsIdx >= 0 &&
          showMetaBar &&
          partIndex === actionsIdx &&
          createdAt && (
            <MessageMetaBar
              createdAt={createdAt}
              content={content}
              isUser={isUser}
            />
          )}
        <ToolInvocationAnchor
          data-tool-call-id={part.toolInvocation.toolCallId}
        >
          <ToolInvocationRenderer
            invocation={part.toolInvocation}
            isDebug={isDebug}
            onApproveToolUse={onApproveToolUse}
            onApplyFrontendTool={onApplyFrontendTool}
            onRejectFrontendTool={onRejectFrontendTool}
            onUserInteraction={onUserInteraction}
            interactionsDisabled={interactionsDisabled}
            getToolRenderer={getToolRenderer}
            contentMaxWidth={contentMaxWidth}
            showToolErrorDetails={showToolErrorDetails}
          />
        </ToolInvocationAnchor>
      </Fragment>
    );
  }

  if (part.type === 'conversation-end') {
    return (
      <FeedbackButtons
        message={message.content}
        eventType={events.AI_CHAT_MESSAGE_FEEDBACK}
      />
    );
  }

  return null;
};

type Props = {
  message?: ChatMessageType;
  isDebug?: boolean;
  onApproveToolUse?: (
    val: boolean,
    interruptId?: string,
    newArgs?: Record<string, unknown>,
  ) => Promise<ApprovalSubmissionResult>;
  onApplyFrontendTool?: (
    toolCallId: string,
    toolName: string,
    args: Record<string, unknown>,
  ) => void | Promise<void>;
  onRejectFrontendTool?: (toolCallId: string) => void | Promise<void>;
  onUserInteraction?: OnUserInteraction;
  interactionsDisabled?: boolean;
  getToolRenderer?: (toolName?: string) => ToolRendererFunction | null;
  contentMaxWidth?: number;
  showToolErrorDetails?: boolean;
  isStreaming?: boolean;
  isTurnComplete?: boolean;
};

const ChatMessage = ({
  message,
  isDebug = false,
  onApproveToolUse,
  onApplyFrontendTool,
  onRejectFrontendTool,
  onUserInteraction,
  interactionsDisabled,
  getToolRenderer,
  contentMaxWidth,
  showToolErrorDetails = false,
  isStreaming = false,
  isTurnComplete = false,
}: Props) => {
  const toolGroupExpandState = useRef(new Map<string, boolean>());

  if (!message) return <Wrapper $maxWidth={contentMaxWidth} />;

  const isUser = message.role === 'user';
  const groupedParts = groupToolParts(
    message.parts ?? [],
    showToolErrorDetails,
  );
  const segments = isUser
    ? [{ chain: [], final: groupedParts }]
    : splitTurnSegments(groupedParts, showToolErrorDetails, isTurnComplete);
  const metaAboveActions = segments.some((segment) =>
    segment.final.some(
      (part) =>
        part.type === 'tool-invocation' &&
        part.toolInvocation.toolName === 'user_actions_display',
    ),
  );
  const { createdAt, content } = message;
  const showMetaBar = !!createdAt && (isUser || !isStreaming);
  const partProps = {
    isUser,
    message,
    showMetaBar,
    isDebug,
    onApproveToolUse,
    onApplyFrontendTool,
    onRejectFrontendTool,
    onUserInteraction,
    interactionsDisabled,
    getToolRenderer,
    contentMaxWidth,
    showToolErrorDetails,
    toolGroupExpandState: toolGroupExpandState.current,
  };

  return (
    <Wrapper $maxWidth={contentMaxWidth}>
      <MessageColumn $isUser={isUser}>
        <Message $isUser={isUser}>
          {segments.map((segment, segmentIdx) => {
            const collapseChain = shouldCollapseSegmentChain(
              segment,
              isTurnComplete,
            );
            const actionsIdx = segment.final.findIndex(
              (part) =>
                part.type === 'tool-invocation' &&
                part.toolInvocation.toolName === 'user_actions_display',
            );
            return (
              <Fragment key={`segment-${segmentIdx}`}>
                {segment.chain.length > 0 &&
                  (shouldWrapChainInThoughtProcess(segment.chain) ? (
                    <ThoughtProcess
                      toolCount={countToolInvocations(segment.chain)}
                      isCollapsed={collapseChain}
                    >
                      {segment.chain.map((part, idx) => (
                        <MessagePart
                          key={`chain-${idx}`}
                          part={part}
                          partIndex={-1}
                          actionsIdx={-1}
                          {...partProps}
                          isCollapsed={collapseChain}
                        />
                      ))}
                    </ThoughtProcess>
                  ) : (
                    segment.chain.map((part, idx) => (
                      <MessagePart
                        key={`chain-${idx}`}
                        part={part}
                        partIndex={-1}
                        actionsIdx={-1}
                        {...partProps}
                        isCollapsed={collapseChain}
                      />
                    ))
                  ))}
                {segment.final.map((part, idx) => (
                  <MessagePart
                    key={`final-${idx}`}
                    part={part}
                    partIndex={idx}
                    actionsIdx={actionsIdx}
                    {...partProps}
                  />
                ))}
              </Fragment>
            );
          })}
        </Message>
        {!metaAboveActions && showMetaBar && createdAt && (
          <MessageMetaBar
            createdAt={createdAt}
            content={content}
            isUser={isUser}
          />
        )}
      </MessageColumn>
    </Wrapper>
  );
};

export default ChatMessage;
